import {
  Grid,
  Icon,
  LinkButton,
  PublicButton,
  SkeletonDots,
  Text,
  useIsMobile
} from '@yes.technology/react-toolkit'

import NavigationSliderSecondary from 'component/NavigationSliderSecondary'
import {
  BulletPoints,
  ButtonWrapper,
  Complement,
  Container,
  Content,
  ContentGrid,
  ContentMiddle,
  ContentText,
  Image,
  ImageWrapper,
  Navigation,
  NavigationColumn,
  NavigationContainer,
  NavigationRow
} from './DynamicInfoCardHighlight.styles'
import { DynamicInfoCardHighlightProps } from './DynamicInforCardHighlight.types'
import { getNavigationColumnAttributes } from './utils'
import { ComponentProps, useCallback } from 'react'

export default function DynamicInfoCardHighlight({
  navigationHeading,
  navigationSliderLabels,
  des,
  image,
  bulletPoints,
  text,
  complement,
  callToAction,
  isLoading = false,
  showNavigationSliders = true,
  onClickNavigationSlider,
  mainNavigationColor = '#6F7AA7',
  secondaryNavigationColor = '#FBC521',
  buttonPosition = 'bottom',
  complementPosition = 'bottom',
  secondaryNavigationDisabled,
  imagePosition,
  imageFormat = 'default',
  bulletPointIconColor,
  alignText
}: DynamicInfoCardHighlightProps) {
  const [mainNavigationLabel, secondaryNavigationLabel] = navigationSliderLabels
  const isMobile = useIsMobile()

  const ActionButton = useCallback(() => {
    if (!callToAction) return null

    return callToAction.type === 'public' ? (
      <PublicButton
        href={callToAction.href}
        disabled={isLoading || callToAction.disabled}
        heightClass={`${isMobile ? 'semiLarge' : 'large'}`}
        style={callToAction.style}
        skeleton={isLoading}
        {...(callToAction as ComponentProps<typeof PublicButton>)}
      >
        {callToAction.label}
      </PublicButton>
    ) : (
      <LinkButton
        href={callToAction.href}
        disabled={isLoading || callToAction.disabled}
        heightClass={`${isMobile ? 'semiLarge' : 'large'}`}
        style={callToAction.style}
      >
        {!isLoading ? callToAction.label : <SkeletonDots />}
      </LinkButton>
    )
  }, [callToAction, isLoading, isMobile])

  const { navigation, navigationSecondary } = getNavigationColumnAttributes({
    hasSecondaryNavigation: !!secondaryNavigationLabel
  })

  const isMobileOrHasOnlyPrimaryNavigation =
    isMobile || !secondaryNavigationLabel

  return (
    <Container>
      {showNavigationSliders && (
        <Grid style={{ width: '100%', padding: 0 }}>
          <Navigation>
            <Text variant='content-emphasis-06' as='h2'>
              {navigationHeading}
            </Text>
            <NavigationRow
              style={{
                justifyContent: isMobileOrHasOnlyPrimaryNavigation
                  ? 'center'
                  : 'flex-start'
              }}
            >
              <NavigationColumn
                {...navigation}
                style={{
                  paddingRight: isMobileOrHasOnlyPrimaryNavigation ? 0 : '12px'
                }}
              >
                <NavigationContainer>
                  <NavigationSliderSecondary
                    currentIndex={0}
                    justifyContent='space-between'
                    options={[
                      {
                        value: mainNavigationLabel,
                        color: mainNavigationColor
                      }
                    ]}
                    onPrevClick={() => onClickNavigationSlider('main-previous')}
                    onNextClick={() => onClickNavigationSlider('main-next')}
                  />
                </NavigationContainer>
              </NavigationColumn>
              {secondaryNavigationLabel && (
                <NavigationColumn
                  {...navigationSecondary}
                  style={{
                    paddingLeft: isMobileOrHasOnlyPrimaryNavigation ? 0 : '12px'
                  }}
                >
                  <NavigationContainer style={{ borderTop: 0 }}>
                    <NavigationSliderSecondary
                      currentIndex={0}
                      justifyContent='space-between'
                      options={[
                        {
                          value: secondaryNavigationLabel,
                          color: secondaryNavigationColor
                        }
                      ]}
                      onPrevClick={() =>
                        onClickNavigationSlider('secondary-previous')
                      }
                      onNextClick={() =>
                        onClickNavigationSlider('secondary-next')
                      }
                      disabled={secondaryNavigationDisabled}
                    />
                  </NavigationContainer>
                </NavigationColumn>
              )}
            </NavigationRow>
          </Navigation>
        </Grid>
      )}
      <ContentGrid>
        <Content>
          {isLoading && <SkeletonDots />}
          {!isLoading && (
            <>
              <h2>{des}</h2>

              {complementPosition === 'top' && !isMobile && complement && (
                <Complement>{complement}</Complement>
              )}

              <ContentMiddle imagePosition={imagePosition}>
                <ImageWrapper imagePosition={imagePosition}>
                  <Image imageFormat={imageFormat}>
                    <img {...image} />
                  </Image>
                </ImageWrapper>

                {complementPosition === 'top' && isMobile && complement && (
                  <Complement>{complement}</Complement>
                )}

                {bulletPoints && (
                  <BulletPoints imagePosition={imagePosition}>
                    {bulletPoints.map((bulletPoint) => (
                      <li key={bulletPoint}>
                        <Icon
                          iconName='Success'
                          {...(bulletPointIconColor
                            ? { hexColorCode: bulletPointIconColor }
                            : { icColor: 'primary' })}
                        />
                        <ContentText>{bulletPoint}</ContentText>
                      </li>
                    ))}
                  </BulletPoints>
                )}

                {text && (
                  <ContentText
                    imagePosition={imagePosition}
                    alignText={alignText}
                  >
                    {text}
                  </ContentText>
                )}

                {(buttonPosition === 'right' || buttonPosition === 'left') &&
                  !isMobile && (
                    <ButtonWrapper imagePosition={imagePosition}>
                      <ActionButton />
                    </ButtonWrapper>
                  )}
              </ContentMiddle>

              {complementPosition === 'bottom' && complement && (
                <Complement>{complement}</Complement>
              )}
            </>
          )}

          {(buttonPosition === 'bottom' || isMobile) && <ActionButton />}
        </Content>
      </ContentGrid>
    </Container>
  )
}
